import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/code-display.default.js';
import * as wireframeSkin from './skins/code-display.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const LS_KEY = 'run-code-display-framework';

class RunCodeDisplay extends RunElement {
  #code = '';
  #language = 'html';
  #frameworks = '';
  #activeFramework = '';
  #showLineNumbers = false;
  #highlightLines = '';
  #rendering = false;
  #isCopying = false;
  #copyTimeout = null;
  #prismRetryQueued = false;

  static get observedAttributes() {
    return ['language', 'frameworks', 'active-framework', 'show-line-numbers', 'highlight-lines'];
  }

  // Arrow-function closure properties capture `this` without rebinding.
  // NEVER use `this.#method = this.#method.bind(this)` — private methods
  // are not writable and the constructor will throw TypeError.
  _onTabChangeBound = (e) => this.#handleTabChange(e);
  _onShadowClickBound = (e) => this.#handleShadowClick(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'region');
    this.#resolveInitialFramework();
    this.shadowRoot.addEventListener('run-tab-show', this._onTabChangeBound);
    this.shadowRoot.addEventListener('click', this._onShadowClickBound);
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.shadowRoot.removeEventListener('run-tab-show', this._onTabChangeBound);
    this.shadowRoot.removeEventListener('click', this._onShadowClickBound);
    if (this.#copyTimeout !== null) {
      clearTimeout(this.#copyTimeout);
      this.#copyTimeout = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'language':
        this.language = newValue !== null ? newValue : 'html';
        break;
      case 'frameworks':
        this.frameworks = newValue !== null ? newValue : '';
        break;
      case 'active-framework':
        this.activeFramework = newValue !== null ? newValue : '';
        break;
      case 'show-line-numbers':
        this.showLineNumbers = newValue !== null;
        break;
      case 'highlight-lines':
        this.highlightLines = newValue !== null ? newValue : '';
        break;
    }
  }

  get code() { return this.#code; }
  set code(value) {
    const coerced = (value !== null && value !== undefined) ? value : '';
    if (this.#code === coerced) return;
    this.#code = coerced;
    if (typeof coerced === 'object' && coerced !== null) {
      this.#resolveInitialFramework();
    }
    this.#updateAria();
    this.requestUpdate();
  }

  get language() { return this.#language; }
  set language(value) {
    const coerced = String(value !== null && value !== undefined ? value : 'html').trim() || 'html';
    if (this.#language === coerced) return;
    this.#language = coerced;
    this.setAttribute('language', coerced);
    this.requestUpdate();
  }

  get frameworks() { return this.#frameworks; }
  set frameworks(value) {
    const coerced = String(value !== null && value !== undefined ? value : '').trim();
    if (this.#frameworks === coerced) return;
    this.#frameworks = coerced;
    this.setAttribute('frameworks', coerced);
    this.requestUpdate();
  }

  get activeFramework() { return this.#activeFramework; }
  set activeFramework(value) {
    const coerced = String(value !== null && value !== undefined ? value : '').trim();
    if (this.#activeFramework === coerced) return;
    this.#activeFramework = coerced;
    this.setAttribute('active-framework', coerced);
    if (coerced) {
      try { localStorage.setItem(LS_KEY, coerced); } catch (_) {}
    }
    this.#updateAria();
    this.requestUpdate();
    this.#emitFrameworkChange(coerced);
  }

  get showLineNumbers() { return this.#showLineNumbers; }
  set showLineNumbers(value) {
    const coerced = Boolean(value);
    if (this.#showLineNumbers === coerced) return;
    this.#showLineNumbers = coerced;
    this.toggleAttribute('show-line-numbers', coerced);
    this.requestUpdate();
  }

  get highlightLines() { return this.#highlightLines; }
  set highlightLines(value) {
    const coerced = String(value !== null && value !== undefined ? value : '').trim();
    if (this.#highlightLines === coerced) return;
    this.#highlightLines = coerced;
    this.setAttribute('highlight-lines', coerced);
    this.requestUpdate();
  }

  // Read-only state exposed to skin modules

  get isCopying() { return this.#isCopying; }

  get isMultiFramework() {
    return typeof this.#code === 'object' && this.#code !== null;
  }

  get resolvedFrameworkOrder() {
    if (!this.isMultiFramework) return [];
    const keys = Object.keys(this.#code);
    if (!this.#frameworks) return keys;
    const declared = this.#frameworks.split(',').map(f => f.trim()).filter(Boolean);
    const ordered = declared.filter(f => keys.includes(f));
    const remaining = keys.filter(k => !ordered.includes(k));
    return [...ordered, ...remaining];
  }

  get resolvedFrameworkTabs() {
    return this.resolvedFrameworkOrder.map(key => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      active: key === this.#activeFramework,
    }));
  }

  get activeCode() {
    if (typeof this.#code === 'string') return this.#code;
    if (this.isMultiFramework) {
      return this.#code[this.#activeFramework] ?? '';
    }
    return '';
  }

  get resolvedLanguage() {
    if (this.isMultiFramework && this.#activeFramework) {
      return this.#activeFramework;
    }
    return this.#language;
  }

  get parsedHighlightLines() {
    if (!this.#highlightLines) return new Set();
    const lines = new Set();
    for (const part of this.#highlightLines.split(',')) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      if (trimmed.includes('-')) {
        const [rawStart, rawEnd] = trimmed.split('-');
        const start = Number(rawStart);
        const end = Number(rawEnd);
        if (!isNaN(start) && !isNaN(end) && start > 0 && end >= start) {
          for (let i = start; i <= end; i++) lines.add(i);
        }
      } else {
        const n = Number(trimmed);
        if (!isNaN(n) && n > 0) lines.add(n);
      }
    }
    return lines;
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
      this.#highlightCode();
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  // Prism's auto-detect scans `document` and cannot see inside shadow roots,
  // so syntax highlighting requires an explicit highlightElement() call after
  // each render. Prism is treated as an optional global rather than an import
  // to keep this design system free of runtime dependencies — consumers load
  // Prism however they prefer (CDN, bundler) and register the languages they
  // need. When Prism is absent the code degrades to plain text.
  #highlightCode() {
    const code = this.shadowRoot.querySelector('pre > code');
    if (!code) return;
    if (this.#highlightLines) {
      code.parentElement.setAttribute('data-line', this.#highlightLines);
    } else {
      code.parentElement.removeAttribute('data-line');
    }
    const Prism = globalThis.Prism;
    if (Prism && typeof Prism.highlightElement === 'function') {
      Prism.highlightElement(code);
      return;
    }
    // Handle deferred-CDN load order: if Prism isn't ready yet, retry once
    // after window load. Without this, code rendered before defer scripts
    // settle would stay un-highlighted forever (no property changes ⇒ no
    // second updated() call).
    if (!this.#prismRetryQueued && document.readyState !== 'complete') {
      this.#prismRetryQueued = true;
      window.addEventListener('load', () => {
        this.#prismRetryQueued = false;
        this.requestUpdate();
      }, { once: true });
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    const lang = this.isMultiFramework && this.#activeFramework
      ? this.#activeFramework
      : this.#language;
    this.setAttribute('aria-label', `Code display: ${lang}`);
  }

  #resolveInitialFramework() {
    if (!this.isMultiFramework) return;
    const order = this.resolvedFrameworkOrder;
    if (!order.length) return;

    let stored = '';
    try { stored = localStorage.getItem(LS_KEY) || ''; } catch (_) {}

    if (stored && order.includes(stored)) {
      // Set private field directly to prevent event dispatch on initialization.
      // setAttribute will trigger attributeChangedCallback → setter, but the
      // early-exit guard (same value already stored) prevents re-entrant dispatch.
      if (this.#activeFramework !== stored) {
        this.#activeFramework = stored;
        this.setAttribute('active-framework', stored);
      }
    } else if (!this.#activeFramework || !order.includes(this.#activeFramework)) {
      const first = order[0] ?? 'html';
      this.#activeFramework = first;
      this.setAttribute('active-framework', first);
      try { localStorage.setItem(LS_KEY, first); } catch (_) {}
    }
  }

  #handleTabChange(event) {
    event.stopPropagation();
    // run-tabs dispatches run-tab-show with detail { name, panel }
    // where name = the panel attribute value on the activated run-tab
    const framework = event.detail?.name ?? '';
    if (!framework || framework === this.#activeFramework) return;
    // Go through the public setter so localStorage is updated and run-framework-change fires.
    this.activeFramework = framework;
  }

  #handleShadowClick(event) {
    // Clicks from inside run-icon-button's shadow root are retargeted to the
    // run-icon-button element when observed at our shadow root level. Match by
    // element name and the part attribute placed by the skin.
    const target = event.target;
    if (
      target.localName === 'run-icon-button' &&
      target.getAttribute('part') === 'copy-button'
    ) {
      event.stopPropagation();
      this.#handleCopyClick();
    }
  }

  #handleCopyClick() {
    if (!navigator.clipboard?.writeText) return;
    const code = this.activeCode;
    navigator.clipboard.writeText(code).then(() => {
      // run-copy fires only after a successful clipboard write.
      this.#emitCopy(code);
      this.#startCopyFeedback();
    }).catch(() => {
      // Clipboard write failed — run-copy must not fire.
    });
  }

  #startCopyFeedback() {
    // Icon switches for exactly 2000ms then reverts.
    if (this.#copyTimeout !== null) {
      clearTimeout(this.#copyTimeout);
    }
    this.#isCopying = true;
    this.requestUpdate();
    this.#copyTimeout = setTimeout(() => {
      this.#isCopying = false;
      this.#copyTimeout = null;
      this.requestUpdate();
    }, 2000);
  }

  #emitFrameworkChange(framework) {
    this.dispatchEvent(new CustomEvent('run-framework-change', {
      bubbles: true,
      composed: true,
      detail: { framework },
    }));
  }

  #emitCopy(code) {
    this.dispatchEvent(new CustomEvent('run-copy', {
      bubbles: true,
      composed: true,
      detail: { code },
    }));
  }
}

if (!customElements.get('run-code-display')) {
  customElements.define('run-code-display', RunCodeDisplay);
}

export { RunCodeDisplay };
export default RunCodeDisplay;
